import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { TEXT_STYLES } from 'TEXT_STYLES'

import { addToTracker } from 'actions/database'
import { productType } from 'types/product'

import {
  Popper,
  PLACEMENT as PLACEMENT_LOOKUP
} from 'ui_elements/Popper/Popper'
import { DashedRowWithButton } from 'ui_elements/DashedRowWithButton/DashedRowWithButton'
import { AddToGroupDropdownForm } from 'ui_elements/AddToGroupDropdownForm/AddToGroupDropdownForm'

const StyledPopper = styled(Popper)`
  width: 244px;
  height: 279px;
  display: flex;
  flex-direction: column;
  border-radius: 2px;
  align-items: flex-start;
  box-shadow: 0 4px 4px 0 ${props => props.theme.colors.coolGrey300};
  padding: 0;

  & > [data-popper-arrow='true'],
  & > [data-popper-arrow='true']:before {
    position: absolute;
    width: 8px;
    height: 8px;
  }

  & > [data-popper-arrow='true']:before {
    content: '';
    transform: rotate(45deg);
    background-color: ${props => props.theme.colors.white};
  }

  &[data-popper-placement^='top'] > [data-popper-arrow='true'] {
    bottom: -4px;
  }

  &[data-popper-placement^='bottom'] > [data-popper-arrow='true'] {
    top: -4px;
  }
`
const DropdownTitle = styled.div`
  ${TEXT_STYLES.BodyBlack};
  color: ${props => props.theme.colors.blackOpacity40};
  margin: 12px;
`
const DropdownWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const DropdownItems = styled.div`
  overflow: auto;
  max-height: 173px;
`
const DropdownItem = styled.div`
  cursor: pointer;
  padding: 4px 12px;
  margin-bottom: 8px;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary100};
  }
`
const ButtonWrapper = styled.div`
  padding: 10px;
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
`
const StyledAddToGroupDropdownForm = styled(AddToGroupDropdownForm)`
  padding: 0 12px;
`

const defaultModifiers = [
  {
    name: 'offset',
    options: {
      offset: [0, 6]
    }
  },
  {
    name: 'arrow',
    options: {
      padding: 8
    }
  }
]

export const AddGroupDropdown = ({
  className,
  popperAnchor,
  isOpen,
  setIsOpen,
  placement,
  product
}) => {
  const { t } = useTranslation(['product_database'])

  const dispatch = useDispatch()

  let trackerCategories = useSelector(
    state => state.globalData.trackerCategories
  )

  trackerCategories = trackerCategories.filter(category => {
    return !['Ungrouped', 'Uncategorized'].includes(category.name)
  })

  const hasMultipleUsers = useSelector(
    state => state.globalData.user.has_multiple_users
  )

  const { isLoading, completed } = useSelector(
    state => state.tracker.createCategory
  )

  const [showForm, setShowForm] = useState(!trackerCategories.length)
  const [isPrivateCategory, setIsPrivateCategory] = useState(!hasMultipleUsers)
  const [inputCategoryName, setInputCategoryName] = useState('')

  const title = showForm
    ? t(
        'product_database:DatabaseTable.modalAddTitle',
        'Create New Product Group'
      )
    : t(
        'product_database:DatabaseTable.modalSelectTitle',
        'Add To Product Group'
      )

  const toggleForm = () => {
    if (showForm) {
      setInputCategoryName('')
    }

    setShowForm(prev => !prev)
  }

  const handlePrivateCategoryChanged = ({ value }) => {
    setIsPrivateCategory(value)
  }

  const renderContent = () => {
    if (showForm) {
      return (
        <StyledAddToGroupDropdownForm
          isBackButtonVisible={trackerCategories.length > 0}
          createButtonText={t(
            'product_database:DatabaseTable.modalCreateButton',
            'Create & Add'
          )}
          renderToggle
          toggleForm={toggleForm}
          handleSubmit={() => {
            const params = {
              product,
              country: product.country,
              category: inputCategoryName,
              isNewCategory: true,
              isPrivateCategory,
              t
            }

            dispatch(addToTracker(params))
          }}
          completed={completed}
          handleInputChange={({ value }) => setInputCategoryName(value)}
          handleToggleChange={handlePrivateCategoryChanged}
          isLoading={isLoading}
          label={t('product_database:DatabaseTable.modalLabel', 'Group Name')}
          value={inputCategoryName}
          hasMultipleUsers={hasMultipleUsers}
          sharingEnabled
          isGroupPrivate={{ isPrivate: isPrivateCategory }}
        />
      )
    }

    return (
      <DropdownItems>
        {trackerCategories.map(category => {
          return (
            <DropdownItem
              key={category.name}
              onClick={() => {
                dispatch(
                  addToTracker({
                    product,
                    category: category.name,
                    t,
                    country: product.country
                  })
                )
                setIsOpen(false)
              }}>
              {category.name}
            </DropdownItem>
          )
        })}
      </DropdownItems>
    )
  }

  return (
    <StyledPopper
      className={className}
      anchor={popperAnchor}
      isOpen={isOpen}
      onClickOutside={() => isOpen && setIsOpen(false)}
      placement={PLACEMENT_LOOKUP[placement]}
      modifiers={defaultModifiers}>
      <div data-popper-arrow />
      <DropdownWrapper>
        <div>
          <DropdownTitle>{title}</DropdownTitle>
          {renderContent()}
        </div>
        {!showForm && (
          <ButtonWrapper>
            <DashedRowWithButton handleClick={toggleForm}>
              {t(
                'product_database:DatabaseTable.modalCreateTitle',
                'Create New Group'
              )}
            </DashedRowWithButton>
          </ButtonWrapper>
        )}
      </DropdownWrapper>
    </StyledPopper>
  )
}

AddGroupDropdown.defaultProps = {
  className: undefined,
  popperAnchor: undefined,
  placement: 'bottomStart'
}

AddGroupDropdown.propTypes = {
  className: PropTypes.string,
  popperAnchor: PropTypes.instanceOf(Element),
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  placement: PropTypes.string,
  product: productType.isRequired
}
